import { useEffect, useRef, useState } from 'react';
import DropdownMenu from './DropdownMenu';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const isItemActive = (item = {}) => {
  if (item.active) return true;

  if (item.url) {
    return false;
  }

  if (Array.isArray(item.children) && item.children.length) {
    return item.children.some((child) => isItemActive(child));
  }

  return false;
};

const isTextNode = (item = {}) => item.tag === 'span' || !item.url;

const mapLinkAttributes = (item = {}) => {
  const { url, newWindow } = item;

  if (!url || item.tag === 'span') return {};

  const attrs = { href: url };

  if (newWindow) {
    attrs.target = '_blank';
    attrs.rel = 'noopener noreferrer';
  }

  return attrs;
};

function useDropdownController() {
  const [openIndex, setOpenIndex] = useState(null);
  const [readyIndex, setReadyIndex] = useState(null);
  const lastTapRef = useRef({ index: null, time: 0 });
  const isTouchLikeRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasHover =
      window.matchMedia &&
      window.matchMedia('(hover: hover)').matches &&
      window.matchMedia('(pointer: fine)').matches;

    isTouchLikeRef.current = !hasHover;
  }, []);

  const bindHandlers = (index, hasChildren, itemUrl) => {
    const handleMouseEnter = () => {
      setOpenIndex(index);
      setReadyIndex(null);
    };

    const handleMouseLeave = () => {
      setOpenIndex(null);
      setReadyIndex(null);
    };


    const handleClick = (event) => {
      if (!hasChildren) return;

      if (!isTouchLikeRef.current) {
        // Desktop/pointer: let the link behave normally.
        return;
      }

      event.preventDefault();

      if (openIndex === index) {
        setOpenIndex(null);
        setReadyIndex(null);
        lastTapRef.current = { index: null, time: 0 };
        return;
      }

      setOpenIndex(index);
      setReadyIndex(index);
      lastTapRef.current = { index, time: Date.now() };
    };

    const handleFocus = () => {
      if (hasChildren) {
        setOpenIndex(index);
        setReadyIndex(index);
      }
    };

    const handleBlur = (event) => {
      if (!hasChildren) return;
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setOpenIndex(null);
        setReadyIndex(null);
        lastTapRef.current = { index: null, time: 0 };
      }
    };

    return {
      isOpen: openIndex === index,
      handleMouseEnter,
      handleMouseLeave,
      handleClick,
      handleFocus,
      handleBlur,
    };
  };

  return { bindHandlers, isTouchLikeRef, openIndex };
}

function NavChevron({ id }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <mask
        id={id}
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="18"
        height="18"
      >
        <rect width="18" height="18" fill="#D9D9D9" />
      </mask>
      <g mask={`url(#${id})`}>
        <path
          opacity="0.42"
          d="M9 11.55L4.5 7.05L5.55 6L9 9.45L12.45 6L13.5 7.05L9 11.55Z"
          fill="#F8F8F7"
        />
      </g>
    </svg>
  );
}

function PrimaryNavigation({ items }) {
  const { bindHandlers, isTouchLikeRef, openIndex } = useDropdownController();
  const triggerRefs = useRef([]);

  useEffect(() => {
    if (openIndex === null) return;
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('header:primary-dropdown-open'));
  }, [openIndex]);

  return (
    <ul className="g-header__primary-navigation-list">
      {items.map((item, index) => {
        const key = `${item.title || 'item'}-${index}`;
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const isActive = isItemActive(item);
        const itemClassName = classNames(
          'g-header__primary-navigation-item',
          hasChildren && 'g-header__primary-navigation-item--has-children',
        );

        const linkClassName = classNames(
          'g-header__primary-navigation-link',
          isActive && 'g-header__primary-navigation-link--active',
        );

        if (!hasChildren) {
          return (
            <li key={key} className={itemClassName}>
              {isTextNode(item) ? (
                <span className={linkClassName} title={item.title || undefined}>
                  {item.title}
                </span>
              ) : (
                <a
                  {...mapLinkAttributes(item)}
                  className={linkClassName}
                  title={item.title || undefined}
                >
                  {item.title}
                </a>
              )}
            </li>
          );
        }

        const { isOpen, handleMouseEnter, handleMouseLeave, handleClick, handleFocus, handleBlur } =
          bindHandlers(index, hasChildren, item.url);
        const isMegaMenuOpen = isOpen;

        return (
          <li
            key={key}
            className={itemClassName}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onBlur={handleBlur}
          >
            {isTextNode(item) ? (
              <button
                type="button"
                className={classNames(
                  linkClassName,
                  isTouchLikeRef.current && isMegaMenuOpen && 'g-header__primary-navigation-link--open',
                )}
                title={item.title || undefined}
                aria-expanded={isMegaMenuOpen}
                aria-haspopup="true"
                onClick={handleClick}
                onFocus={handleFocus}
                ref={(el) => {
                  triggerRefs.current[index] = el;
                }}
              >
                {item.title}
                <NavChevron id={`header-nav-chevron-${index}`} />
              </button>
            ) : (
              <a
                {...mapLinkAttributes(item)}
                className={classNames(
                  linkClassName,
                  isTouchLikeRef.current && isMegaMenuOpen && 'g-header__primary-navigation-link--open',
                )}
                title={item.title || undefined}
                aria-expanded={isMegaMenuOpen}
                onClick={handleClick}
                onFocus={handleFocus}
                ref={(el) => {
                  triggerRefs.current[index] = el;
                }}
              >
                {item.title}
                <NavChevron id={`header-nav-chevron-${index}`} />
              </a>
            )}
            <DropdownMenu
              item={item}
              baseKey={key}
              mapLinkAttributes={mapLinkAttributes}
              useGrid
              isOpen={isMegaMenuOpen}
              allowNested
            />
          </li>
        );
      })}
    </ul>
  );
}

export default function HeaderPrimaryNavigation({
  items = [],
}) {
  return (
    <PrimaryNavigation items={items} />
  );
}
